import { ContentLink } from "@robe/client/xcore-ui";
import { Box, Button, Container, Flex, Img, Link as UILink, Stack, Typography } from "@xcorejs/ui";
import InnovationBox, { InnovationBoxInnovation } from "components/InnovationBox";
import SpecificationBox from "components/Product/SpecificationBox";
import { ProgressBarStyle } from "components/ProgressBar";
import RobeLoader from "components/RobeLoader";
import { Strong } from "design-system";
import DropdownItem from "design-system/xcore/Dropdown/DropdownItem";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useLayout } from "xcore";
import { ProductOverviewPage } from "xcore/types";
import { resolveSrcSet } from "utils";
import { SubHeaderContent, SubHeaderContentsRes } from "pages/api/robe/subheader";

interface SubheaderItemDropdownProps {
  active: boolean;
  pop?: ProductOverviewPage;
  empty?: boolean;
}

const SubheaderItemDropdown = ({ active, pop, empty }: SubheaderItemDropdownProps) => {
  const { stringify, general, richtextToString, localize, image } = useLayout();
  const [subheaderContent, setSubheaderContent] = useState<SubHeaderContent[] | null>(null);
  const [showLoader, setShowLoader] = useState(false);
  const [activeItem, setActiveItem] = useState(subheaderContent?.[0]?.id);

  useEffect(() => {
    setTimeout(() => setShowLoader(true), 500);
  }, [active]);

  useEffect(() => {
    if (pop) {
      setShowLoader(false);

      const items = localize(pop?.values.itemsRelation) as SubHeaderContentsRes | null;
      const itemsFiltered = items ? items.filter((item) => item && item?.id && item?.type && item?.slug && item?.values) : [];

      setSubheaderContent(itemsFiltered);
      setActiveItem(itemsFiltered?.[0]?.id);
    }
  }, [pop]);

  const item = useMemo(() => subheaderContent?.find((c) => c.id === activeItem)!, [subheaderContent, activeItem]);

  const itemImgResolved =
    item?.values &&
    "images" in item.values &&
    // todo xcc after localized images migrated remove workaround
    (item.values?.images?.en && item.values?.images?.en?.length
      ? item.values?.images?.en
      : item.values?.images && localize(item.values?.images) && localize(item.values?.images)?.length
        ? localize(item.values?.images)
        : null);

  const [detailImage, detailImageEnlarged] = itemImgResolved
    ? [image(itemImgResolved![0], { width: 452, height: 452 }), image(itemImgResolved![0], { width: 452, height: 452, enlargement: 2 })]
    : [null, null];

  if (empty) {
    return null;
  }

  return (
    <Box position="absolute" left={0} right={0} height="100%" opacity={active ? 1 : 0} pointerEvents={active ? null : "none"}>
      <Container
        px={0}
        py={0}
        background="#fff"
        maxHeight="100%"
        overflow="auto"
        minHeight="calc(100vh - 18rem)"
        boxShadow="0 0.5rem 1rem 0 rgba(30, 30, 30, 0.5)"
      >
        {pop ? (
          <Flex alignItems="flex-start" position="relative" bg="#fff" px="1.5rem" py="3rem" height="100%" zIndex={1004}>
            <Flex flexDirection="column">
              {subheaderContent?.map((p) => {
                return (
                  <ContentLink key={p.id} content={{ id: p.id, type: p.type, slug: p.slug }}>
                    <DropdownItem
                      width="30rem"
                      onMouseEnter={() => setActiveItem(p.id)}
                      {...(p.id === activeItem && { bg: "lightGray", fontWeight: 600, color: "black" })}
                    >
                      {stringify(p.title)}
                    </DropdownItem>
                  </ContentLink>
                );
              })}
            </Flex>
            {item?.values && (
              <Flex>
                {detailImage && (
                  <Flex flex={{ _: "0 0 31.5rem", lg: "0 0 45rem" }} maxWidth={{ _: "31.5rem", sm: "45rem" }} mr="3rem">
                    <ContentLink content={{ id: item.id, type: item.type ?? "", slug: item.slug }}>
                      <UILink as="span" display="flex" alignItems="flex-start">
                        <Img
                          display="block"
                          width="100%"
                          mx="auto"
                          maxWidth="100%"
                          height="auto"
                          loading="lazy"
                          src={detailImage}
                          {...resolveSrcSet(detailImage, detailImageEnlarged)}
                          alt={detailImage}
                        />
                      </UILink>
                    </ContentLink>
                  </Flex>
                )}
                <Flex flexDirection="column" height="100%">
                  <Typography variant="h3">{stringify(item.title)}</Typography>
                  <Stack mt="2rem" direction="column" gap="1rem" px={{ _: "1.5rem", sm: "0" }}>
                    {"paramsOrFeatures" in item.values &&
                      item.values.paramsOrFeatures?.map((p, i) => {
                        const imgUrl = p.type?.imageInfos ? image(p.type?.imageInfos[0]) : null;
                        if (!imgUrl) {
                          return <Fragment key={i} />;
                        }

                        return (
                          <SpecificationBox
                            key={i}
                            name={stringify(p.type!.title)}
                            description={richtextToString(p.description)}
                            icon={imgUrl}
                          />
                        );
                      })}
                  </Stack>

                  {"innovations" in item.values && item.values.innovations && (
                    <>
                      <Strong display="block" mt="2rem" mb="1rem" lineHeight="2rem">
                        Innovations
                      </Strong>

                      <Flex flexWrap="wrap" mt="-1rem">
                        {item.values.innovations?.map((i) => (
                          <InnovationBox key={i.id} innovation={i as InnovationBoxInnovation} mt="1rem" mr="1rem" openAs="link" />
                        ))}
                      </Flex>
                    </>
                  )}

                  <ContentLink content={{ id: item.id, type: item.type ?? "", slug: item.slug }}>
                    <Button as="div" alignSelf="flex-start" mt="3rem" whiteSpace="nowrap">
                      {stringify(general.values.productDetailBtn)}
                    </Button>
                  </ContentLink>
                </Flex>
              </Flex>
            )}
          </Flex>
        ) : (
          <Flex position="relative" width="100%" flexDirection="column">
            <Box width="100%" height="0.2rem" zIndex={1006}>
              <ProgressBarStyle bg="primary" height="100%" animation="load 1s forwards" />
            </Box>
            <Flex alignItems="center" justifyContent="center" height="100%" width="100%">
              {showLoader && <RobeLoader width="38.4rem" />}
            </Flex>
          </Flex>
        )}
      </Container>
    </Box>
  );
};

export default SubheaderItemDropdown;
